import React, { Suspense } from 'react';

import { injectGlobal } from '@emotion/css';
import { createRoot } from 'react-dom/client';
import 'react-toastify/dist/ReactToastify.min.css';

import { AppReady } from '@/AppReady.tsx';
import { KeycloakAuth } from '@/auth/KeycloakAuth.tsx';
import { LoadingScreen } from '@/components/loading/LoadingScreen.tsx';
import { CustomToastContainer } from '@/components/toast/Toast';
import { I18nWrapper } from '@/intl/i18n.tsx';
import { TrackingWrapper } from '@/observation_of_users/TrackingWrapper.tsx';
import { QueriesWrapper } from '@/queries/QueriesWrapper.tsx';
import { MuiWrapper } from '@/theme/MuiWrapper.tsx';
import '@/utils/stringUtils';

const container = document.getElementById('root');

/**
 * Component order:
 * - MuiWrapper: Material-UI theme initialization, must be before Suspense since LoadingScreen relies on MUI
 * - Suspense : Handles loading states for lazy-loaded components
 * - TODO add error boundary ?
 * - I18nextProvider : Provides internationalization context
 * - KeycloakAuth: Handles authentication and authorization
 * - TrackingWrapper : Initializes user tracking with PostHog, requires being authenticated
 * - StrictMode: Ensures components follow React's best practices,
 *        so high because KeycloakAuth and TrackingWrapper don't follow best practices
 * - CustomToastContainer: Global toast notifications
 * - QueriesWrapper : Tanstack Query client initialization
 * - AppReady : App is ready to run
 */
createRoot(container as HTMLElement).render(
  <MuiWrapper>
    <Suspense fallback={<LoadingScreen />}>
      <I18nWrapper>
        <KeycloakAuth>
          <TrackingWrapper>
            <React.StrictMode>
              <CustomToastContainer />
              <QueriesWrapper>
                <AppReady />
              </QueriesWrapper>
            </React.StrictMode>
          </TrackingWrapper>
        </KeycloakAuth>
      </I18nWrapper>
    </Suspense>
  </MuiWrapper>,
);

injectGlobal`
    input, a, fieldset, p, ul { all: unset; }
    
    button { all: unset; cursor: pointer; }
    
    html, body, .root {
        width: 100%;
        height: 100%;
    }
`;
